<script setup lang="ts">
import { useRelationsStore } from '@/stores/relations';
import { useUserStore } from '@/stores/user';
import { TextAccordion } from '@magma-app/magma-lapilli';
import RelationContactBlock from '@/components/relations/RelationContactBlock.vue';
import { computed, ref, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useI18n } from 'vue-i18n';

const relationsStore = useRelationsStore();
const userStore = useUserStore();

const route = useRoute();

const isInitialized = ref(false);

const { t } = useI18n();

const relation = computed(() => relationsStore.relationHelper);
const prospect = computed(() => relation.value?.relation?.prospect);
const relationMoment = computed(() => relation.value?.relation?.relationMoment);

watchEffect(async () => {
  isInitialized.value = false;

  await userStore.getUser();

  await relationsStore.getRelation({
    relationId: parseInt(route?.params.relationId as string),
    type: 'helper',
  });

  isInitialized.value = true;

  const driverObj = driver({
    showProgress: true,
    showButtons: ['next', 'close'],
    popoverClass: 'magma-tour',
    progressText: '',
    onPopoverRender: (popover, opts) => {
      const currentStep = opts.state.activeIndex;
      popover.progress.innerHTML = `
				<button class="${currentStep === 0 ? ' is-active' : ''}"></button>
				<button class="${currentStep === 1 ? ' is-active' : ''}"></button>
				<button class="${currentStep === 2 ? ' is-active' : ''}"></button>
				<button class="${currentStep === 3 ? ' is-active' : ''}"></button>
			`;
    },

    nextBtnText: t('startRelationTour.next'),
    prevBtnText: t('startRelationTour.prev'),
    doneBtnText: t('startRelationTour.finish'),

    steps: [
      {
        element: '.start-tour',
        popover: {
          title: t('startRelationTour.first.title'),
          description: t('startRelationTour.first.subtitle'),
        },
      },
      {
        element: '.step-2',
        popover: {
          title: t('startRelationTour.second.title'),
          description: t('startRelationTour.second.subtitle'),
          side: 'bottom',
          align: 'center',
        },
      },
      {
        element: '.step-3',
        popover: {
          title: t('startRelationTour.third.title'),
          description: t('startRelationTour.third.subtitle'),
          side: 'top',
          align: 'center',
        },
      },
      {
        element: '.step-4',
        popover: {
          title: t('startRelationTour.fourth.title'),
          description: t('startRelationTour.fourth.subtitle'),
          side: 'top',
          align: 'center',
        },
      },
    ],
  });

  if (relationsStore?.relationHelper?.isFirstRelation) {
    driverObj.drive();
  }
});
</script>

<template>
  <div
    v-if="isInitialized"
    class="start-tour w-full sm:h-[calc(100dvh_-_64px)] max-h-[100dvh] bg-background-subtle-hover sm:flex justify-center sm:items-center flex-col overflow-y-hidden box-border"
  >
    <div
      class="w-full sm:rounded-xl sm:border border-border-subtle sm:w-[390px] h-dvh sm:h-auto bg-white sm:overflow-hidden sm:shadow-lg flex flex-col relative"
    >
      <header class="w-full h-16 flex items-center justify-between px-5">
        <div class="flex items-center gap-2">
          <img
            :src="userStore.user?.organization?.logoUrl || ''"
            class="w-8 h-8 rounded-full"
          />
          <p
            class="text-mgm-txt-sm text-foreground-emphasis font-medium truncate"
          >
            {{ userStore.campaignInfo?.organizationName }}
          </p>
        </div>
        <img
          :src="userStore.user?.profilePictureUrl || ''"
          class="w-8 h-8 rounded-full"
        />
      </header>
      <div
        class="w-full h-44 sm:h-40 bg-background-brand-default flex items-center justify-center"
      >
        <img
          :src="
            relationsStore.relationHelper?.relation?.prospect.profilePictureUrl
          "
          class="w-16 h-16 rounded-full border border-border-default shadow-lg -mt-3 sm:mt-0"
        />
      </div>

      <div
        class="rounded-t-2xl bg-white pt-6 pb-24 px-5 overflow-y-auto z-10 flex-1 bottom-0 -mt-3 w-full"
      >
        <div class="step-2">
          <h1
            class="text-mgm-txt-xl font-medium text-foreground-emphasis mb-3"
            data-intro="Hello step one!"
          >
            {{ $t('helperStartRelation.meet') }}
            {{ relationsStore.relationHelper?.relation?.prospect.firstname }}
          </h1>

          <section
            v-if="
              relationsStore.relationHelper?.relation?.prospect.expectation !==
              ''
            "
            class="mb-3"
          >
            <p class="text-mgm-txt-sm text-foreground-default">
              {{ $t('helperStartRelation.qualification') }}
            </p>
            <TextAccordion
              :content="
                relationsStore.relationHelper?.relation?.prospect.expectation ||
                ''
              "
              :see-more-button="$t('generic.seeMore')"
              :see-less-button="$t('generic.seeLess')"
            />
          </section>

          <section
            v-for="criteria in relationsStore.relationHelper?.relation?.prospect
              .criteria"
            :key="criteria.labelHelpee"
            class="mb-3"
          >
            <p class="text-mgm-txt-sm text-foreground-default">
              {{ criteria.labelHelpee }}
            </p>
            <p class="text-mgm-txt-sm text-foreground-emphasis font-medium">
              {{ criteria.value }}
            </p>
          </section>

        </div>

        <RelationContactBlock
          :touch-points="prospect?.touchPoints || []"
          :prospect-firstname="prospect?.firstname || ''"
          :welcome-message="prospect?.welcomeMessage || ''"
          :relation-moment-id="relationMoment?.relationMomentId || 0"
          :is-ongoing="!!relationMoment?.helper?.isOngoing"
          :disabled="!!relation?.isReported"
          message-first
          message-class="step-3"
          channels-class="step-4"
        />
      </div>
    </div>
    <div class="hidden sm:block">
      <a
        class="mx-auto mt-8 text-center text-mgm-txt-xs font-normal text-border-default hover:text-foreground-subtle focus:text-foreground-subtle flex gap-2 items-center"
        href="https://www.magma.app/"
        target="_blank"
      >
        {{ $t('generic.powered') }}
        <img src="/img/logo_magma_full_bw.svg" alt="Magma" />
      </a>
    </div>
  </div>
</template>

<style>
.driver-popover.magma-tour {
  background-color: theme('colors.white');
  border-radius: theme('borderRadius.xl');
  padding: theme('spacing.4');
  width: calc(100% - 40px);
  max-width: none !important;
}

@media screen and (min-width: 640px) {
  .driver-popover.magma-tour {
    width: 350px;
  }
}

.driver-popover.magma-tour .driver-popover-title {
  color: #1c1f27;
  font-size: theme('fontSize.sm');
  font-weight: 500 !important;
  width: 66%;
}

.driver-popover.magma-tour .driver-popover-close-btn {
  top: 12px !important;
  right: 8px !important;
}

.driver-popover.magma-tour .driver-popover-description {
  color: #5f6c85;
  font-size: theme('fontSize.sm');
  font-weight: theme('fontWeight.normal');
}

.driver-popover.magma-tour .driver-popover-progress-text {
  display: flex !important;
  align-items: center;
  gap: 8px;
}

.driver-popover.magma-tour .driver-popover-progress-text button {
  background-color: #a4bcfd;
  border-radius: 100px;
  height: 8px;
  width: 8px;
  border: none;
  display: flex;
  margin: 0;
  padding: 0;
}

.driver-popover.magma-tour .driver-popover-progress-text button.is-active {
  background-color: #444ce7;
}

.driver-popover.magma-tour .driver-popover-next-btn {
  background-color: #444ce7;
  border-radius: 100px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  padding: 8px 16px;
  border: none;
  text-shadow: none;
}
</style>
